import { useNavigate } from 'react-router';
import { DotsThreeIcon, StackSimpleIcon } from '@phosphor-icons/react';
import {
  Badge,
  Button,
  Cell,
  CopyButton,
  HintLabel,
  Image,
  Menu,
  type MenuAction,
  Row,
  RowExpandButton,
} from '@skupreme/shared/ui';
import { ItemTitleCell } from '@features/catalog';
import {
  cartonSize,
  cartonWeight,
  unitsPerCarton,
} from '@features/catalog-carton';
import { CommonUtility } from '@utility/common';
import { ItemStates } from '@utility/constants/common';
import { CatalogHelpText } from '@utility/constants/strings';
import { DateFormat, DateUtility } from '@utility/date';
import type { ReactNode } from 'react';
import { sizedImage } from '../../../../lib/listingImage';
import { isCompleteCarton } from '../../lib/carton-alias';
import {
  aliasedVariants,
  type CartonAttribute,
  type CartonQuantities,
  hasManyAliases,
  type InventoryColumn,
  type InventoryFlags,
  type InventoryItem,
  type InventoryVariant,
  type LooseQuantities,
  latestVariant,
  quantityView,
  type WmsData,
} from '../../lib/inventory';
import { PermissionGate } from '../PermissionGate';
import { type Figure, Figures } from './Figures';
import { LotsPopover } from './LotsPopover';
import {
  CartonsPart,
  LoosePart,
  MissingProperties,
  QuantityTotal,
  VariantQuantity,
} from './QuantityCells';
import { ShipmentsBreakdown } from './ShipmentsBreakdown';

/* The API sends the item's picture as `image_url`; `item_images` is the older shape. */
type ImagedItem = InventoryItem & { image_url?: string };

export type AliasRemoval = {
  item: InventoryItem;
  alias: string;
  /** The alias row of an item with several, not the item's own row. */
  isVariant: boolean;
};

/** What every row reads and calls back into; the tab owns it all. */
export type InventoryRowContext = {
  providerId: string;
  flags: InventoryFlags;
  columns: InventoryColumn[];
  readOnly: boolean;
  imperial: boolean;
  displaySetting?: string;
  isApiFtp: boolean;
  isLogisticsPlus: boolean;
  /** Aliases can be removed (not on FBA or AWD, which report them). */
  canRemoveAlias: boolean;
  pureFulfillable: boolean;
  permission: { disabled: boolean; hidden: boolean };
  cartons: CartonQuantities;
  loose: LooseQuantities;
  /** Items saved this visit that the provider is still processing. */
  processing: Set<string>;
  onceSubmitted: boolean;
  expanded: Set<string>;
  onToggle: (itemId: string) => void;
  onCartonsChange: (itemId: string, value: string, variantId?: string) => void;
  onLooseChange: (itemId: string, value: string) => void;
  /** Opens the carton set-up of the item's `index`-th alias. */
  onEdit: (item: InventoryItem, index: number) => void;
  onAddAlias: (item: InventoryItem) => void;
  onSettings: (item: InventoryItem) => void;
  onRemoveAlias: (removal: AliasRemoval) => void;
};

const count = (value?: number) => CommonUtility.numberWithCommas(value || 0);

const expiry = (date?: string | null) =>
  date ? DateUtility.formatDate(date, DateFormat.barChartMonth) : null;

/** Expiry, lot and pallet of what the provider's WMS holds. */
function WmsLines({
  wms,
  lots,
  showLots,
}: {
  wms?: WmsData;
  lots?: InventoryVariant['lots'];
  showLots: boolean;
}) {
  const rows: Figure[] = [];
  const expiryDate = expiry(wms?.expiryDate);

  if (expiryDate) rows.push({ label: 'Expiry', value: expiryDate });
  if (!showLots && wms?.lotNumber) {
    rows.push({ label: 'Lot #', value: wms.lotNumber });
  }

  return (
    <>
      {rows.length ? <Figures rows={rows} /> : null}
      {showLots && lots ? <LotsPopover lots={lots} /> : null}
      {!showLots && wms?.lotNumber && wms.isPallet ? (
        <Badge>
          <StackSimpleIcon size={14} aria-hidden />
          Pallet
        </Badge>
      ) : null}
    </>
  );
}

/** Alias, carton size, units per carton and gross weight. */
function CartonDetails({
  attribute,
  imperial,
  showAlias = true,
}: {
  attribute: CartonAttribute;
  imperial: boolean;
  showAlias?: boolean;
}) {
  const alias = attribute as Parameters<typeof cartonSize>[0];

  return (
    <Figures
      rows={[
        ...(showAlias
          ? [{ label: 'Alias', value: attribute.alias || '-' }]
          : []),
        { label: 'Carton Size', value: cartonSize(alias, imperial) },
        { label: 'Units Per Carton', value: unitsPerCarton(alias) },
        {
          label: 'Gross Weight',
          value: cartonWeight(alias.carton_gross_weight, imperial),
        },
      ]}
    />
  );
}

function RowMenu({
  label,
  actions,
  context,
  onAction,
}: {
  label: string;
  actions: MenuAction[];
  context: InventoryRowContext;
  onAction: (id: string) => void;
}) {
  if (!actions.length) return null;

  const { disabled, hidden } = context.permission;

  return (
    <PermissionGate disabled={disabled} hidden={hidden} placement="left">
      <Menu
        aria-label={label}
        trigger={
          <Button
            variant="ghost"
            shape="circle"
            aria-label={label}
            isDisabled={disabled}
            className="sk:size-6 sk:opacity-0 sk:group-hover:opacity-100 sk:focus-visible:opacity-100 sk:aria-expanded:opacity-100"
          >
            <DotsThreeIcon size={16} weight="bold" />
          </Button>
        }
        actions={actions}
        onAction={onAction}
      />
    </PermissionGate>
  );
}

function Unfulfillable({
  damaged,
  refurbishable,
  allocated,
}: {
  damaged: number;
  refurbishable: number;
  allocated?: number;
}) {
  return (
    <Figures
      rows={[
        { label: 'Damaged', value: count(damaged) },
        { label: 'Refurbishable', value: count(refurbishable) },
        ...(allocated === undefined
          ? []
          : [{ label: 'Allocated Qty', value: count(allocated) }]),
      ]}
    />
  );
}

/** Reserved units: FBA's by reason, a figure elsewhere. */
function ReservedUnits({
  item,
  flags,
}: {
  item: InventoryItem;
  flags: InventoryFlags;
}) {
  if (flags.isFBA) {
    return (
      <HintLabel
        hint={
          <Figures
            rows={[
              {
                label: 'Pending Customer Orders',
                value: item.reservedPendingCustomerOrderQuantity,
              },
              {
                label: 'Warehouse Processing',
                value: item.reservedWarehouseProcessingQuantity,
              },
              {
                label: 'FC Transfer',
                value: item.reservedWarehouseTransferQuantity,
              },
            ]}
          />
        }
      >
        <span className="sk:tabular-nums">{count(item.reservedQuantity)}</span>
      </HintLabel>
    );
  }

  return (
    <span className="sk:tabular-nums">
      {count(flags.isDeliverr ? item.reservedQuantity : item.inbound_qty)}
    </span>
  );
}

/** The item row's carton column: its set-up, or what is missing from it. */
function CartonSetup({
  item,
  context,
  isMissing,
}: {
  item: InventoryItem;
  context: InventoryRowContext;
  isMissing: boolean;
}) {
  const latest = latestVariant(item);

  if (hasManyAliases(item)) {
    return <span className="sk:text-fg-secondary">Multiple Aliases</span>;
  }

  if (isMissing) {
    if (latest.attribute.alias) {
      return (
        <MissingProperties
          onPress={() =>
            context.onEdit(
              { ...item, variants: [latest as InventoryVariant] },
              0,
            )
          }
          isDisabled={context.permission.disabled || context.readOnly}
        />
      );
    }

    return (
      <PermissionGate
        disabled={context.permission.disabled}
        hidden={context.permission.hidden}
      >
        <Button
          size="sm"
          onPress={() => context.onAddAlias(item)}
          isDisabled={context.permission.disabled}
        >
          Add Alias
        </Button>
      </PermissionGate>
    );
  }

  return (
    <CartonDetails attribute={latest.attribute} imperial={context.imperial} />
  );
}

/** An item, and under it, while open, each alias it is held under. */
export function ItemRows({
  item,
  context,
}: {
  item: InventoryItem;
  context: InventoryRowContext;
}) {
  const navigate = useNavigate();
  const { columns, flags, readOnly } = context;
  const latest = latestVariant(item);
  const isMultiple = hasManyAliases(item);
  const isInConflict = !item.upc;
  const isExpanded = context.expanded.has(item.item_id);
  const canEdit = !!latest.attribute.alias && !isMultiple;
  const isMissing =
    (!latest.attribute.alias || !isCompleteCarton(latest.attribute)) &&
    !isMultiple &&
    !readOnly;
  const view = quantityView(item, {
    flags,
    readOnly,
    cartons: context.cartons[item.item_id],
    loose: context.loose[item.item_id],
  });
  const showLots = !isMultiple && readOnly && (latest.lots?.length ?? 0) > 1;

  let damaged = 0;
  let refurbishable = 0;
  for (const variant of item.variants ?? []) {
    damaged += variant.inventory_summary?.damagedQuantity || 0;
    refurbishable += variant.inventory_summary?.refurbishableQuantity || 0;
  }

  const actions: MenuAction[] = [{ id: 'settings', label: 'Item Settings' }];
  if (columns.includes('carton') && !isMissing && !readOnly && !isInConflict) {
    actions.push({ id: 'add', label: 'Add Alias' });
  }
  if (!isInConflict && canEdit) {
    actions.push({ id: 'edit', label: 'Edit Carton' });
  }
  if (context.canRemoveAlias && canEdit) {
    actions.push({
      id: 'remove',
      label: 'Delete Alias',
      isDanger: true,
      startsGroup: true,
    });
  }

  const onAction = (id: string) => {
    if (id === 'settings') context.onSettings(item);
    if (id === 'add') context.onAddAlias(item);
    if (id === 'edit') {
      context.onEdit({ ...item, variants: [latest as InventoryVariant] }, 0);
    }
    if (id === 'remove' && latest.attribute.alias) {
      context.onRemoveAlias({
        item,
        alias: latest.attribute.alias,
        isVariant: false,
      });
    }
  };

  const cells: Record<InventoryColumn, ReactNode> = {
    product: (
      <div className="sk:flex sk:items-start sk:gap-3">
        <Image
          src={sizedImage(
            (item as ImagedItem).image_url ?? item.item_images?.display_image,
            'thumb',
          )}
          alt=""
          size="md"
          fit="contain"
          loading="lazy"
        />
        <div className="sk:flex sk:min-w-0 sk:flex-1 sk:flex-col sk:gap-1">
          <ItemTitleCell
            item={{
              _id: item.item_id,
              p_sku: item.item_sku,
              p_title: item.item_title,
              state: isInConflict ? ItemStates.conflict : item.state,
              is_bundle: item.is_bundle,
              is_duplicate_listing: item.is_duplicate_listing,
            }}
            identifiers={[{ label: 'UPC', value: item.upc }]}
          />
          {item.storage_class ? (
            <span className="sk:text-xs sk:text-fg-tertiary">
              Storage Class {item.storage_class}
            </span>
          ) : null}
          {isMultiple ? null : (
            <WmsLines
              wms={latest.wms_data}
              lots={latest.lots}
              showLots={showLots}
            />
          )}
        </div>
        {isMultiple ? (
          <RowExpandButton
            isExpanded={isExpanded}
            onExpandedChange={() => context.onToggle(item.item_id)}
            aria-label={`${isExpanded ? 'Hide' : 'Show'} aliases of ${item.item_sku}`}
          />
        ) : null}
      </div>
    ),
    unfulfillable: (
      <Unfulfillable
        damaged={damaged}
        refurbishable={refurbishable}
        allocated={item.reservedWarehouseProcessingQuantity ?? 0}
      />
    ),
    outbound: (
      <ShipmentsBreakdown
        item={item}
        providerId={context.providerId}
        inbound={false}
      />
    ),
    inbound: (
      <ShipmentsBreakdown item={item} providerId={context.providerId} inbound />
    ),
    cartons: (
      <div className="sk:flex sk:flex-col sk:gap-1">
        <div className="sk:flex sk:items-start sk:gap-3">
          <CartonsPart item={item} view={view} context={context} />
          {columns.includes('loose') ? null : (
            <LoosePart item={item} view={view} context={context} />
          )}
        </div>
        <QuantityTotal item={item} view={view} context={context} />
      </div>
    ),
    loose: <LoosePart item={item} view={view} context={context} />,
    reserved: <ReservedUnits item={item} flags={flags} />,
    carton: <CartonSetup item={item} context={context} isMissing={isMissing} />,
    actions: (
      <RowMenu
        label={`Actions for ${item.item_sku}`}
        actions={actions}
        context={context}
        onAction={onAction}
      />
    ),
  };

  return (
    <>
      <Row
        id={item.item_id}
        onAction={() =>
          navigate(`/app/catalogs/catalog/${encodeURIComponent(item.item_id)}`)
        }
      >
        {columns.map(column => (
          <Cell
            key={column}
            className={column === 'actions' ? 'sk:text-right' : undefined}
          >
            {cells[column]}
          </Cell>
        ))}
      </Row>
      {isMultiple && isExpanded
        ? aliasedVariants(item).map((variant, index) => (
            <VariantRow
              key={variant._id}
              item={item}
              variant={variant}
              index={index}
              isInConflict={isInConflict}
              context={context}
            />
          ))
        : null}
    </>
  );
}

/** One alias of an item held under several. */
function VariantRow({
  item,
  variant,
  index,
  isInConflict,
  context,
}: {
  item: InventoryItem;
  variant: InventoryVariant;
  index: number;
  isInConflict: boolean;
  context: InventoryRowContext;
}) {
  const hasCarton = CommonUtility.isValidObject(variant.attribute ?? {});
  const alias = variant.alias ?? '';
  const reserved =
    variant.inventory_summary?.inboundWorkingQuantity ||
    variant.reservedQuantity ||
    0;
  const outbound =
    variant.inventory_summary?.outboundQuantity ||
    variant.outboundQuantity ||
    0;

  const actions: MenuAction[] = [];
  if (!isInConflict) actions.push({ id: 'edit', label: 'Edit Carton' });
  if (context.canRemoveAlias) {
    actions.push({ id: 'remove', label: 'Delete Alias', isDanger: true });
  }

  const cells: Record<InventoryColumn, ReactNode> = {
    product: (
      <div className="sk:flex sk:flex-col sk:gap-1">
        <span className="sk:text-xs sk:text-fg-tertiary">
          <HintLabel hint={CatalogHelpText.carton}>
            {variant.primary ? 'Primary Carton Alias' : 'Carton Alias'}
          </HintLabel>
        </span>
        <span className="sk:flex sk:items-center sk:gap-1 sk:font-medium">
          {alias}
          <CopyButton value={alias} label="Copy alias" />
        </span>
        <WmsLines
          wms={variant.wms_data}
          lots={variant.lots}
          showLots={context.readOnly && (variant.lots?.length ?? 0) > 1}
        />
      </div>
    ),
    unfulfillable: (
      <Unfulfillable
        damaged={variant.inventory_summary?.damagedQuantity || 0}
        refurbishable={variant.inventory_summary?.refurbishableQuantity || 0}
      />
    ),
    outbound: <span className="sk:tabular-nums">{count(reserved)}</span>,
    inbound: <span className="sk:tabular-nums">{count(outbound)}</span>,
    cartons: (
      <VariantQuantity
        item={item}
        variant={variant}
        index={index}
        context={context}
      />
    ),
    loose: null,
    reserved: <span className="sk:tabular-nums">{count(reserved)}</span>,
    carton: hasCarton ? (
      <CartonDetails
        attribute={variant.attribute ?? {}}
        imperial={context.imperial}
        showAlias={false}
      />
    ) : (
      <MissingProperties onPress={() => context.onEdit(item, index)} />
    ),
    actions: (
      <RowMenu
        label={`Actions for ${alias}`}
        actions={actions}
        context={context}
        onAction={id => {
          if (id === 'edit') context.onEdit(item, index);
          if (id === 'remove') {
            context.onRemoveAlias({ item, alias, isVariant: true });
          }
        }}
      />
    ),
  };

  return (
    <Row id={`${item.item_id}/${variant._id}`} isNested>
      {context.columns.map(column => (
        <Cell
          key={column}
          className={column === 'actions' ? 'sk:text-right' : undefined}
        >
          {cells[column]}
        </Cell>
      ))}
    </Row>
  );
}
